import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";

const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
};

let superAdminClientListCache = { key: "", data: undefined };
let sharedGatewayLoadPromise = null;

export default function useClients() {
  const {
    api,
    api2,
    adminId,
    setGateWay,
    AllClients,
    GetAllClientList,
    loading,
  } = useContext(GlobalContext);

  const [busy, setBusy] = useState({});

  const setSpin = (id, action) => setBusy((p) => ({ ...p, [id]: action }));
  const clearSpin = (id) => setBusy((p) => ({ ...p, [id]: null }));

  const fetchAll = GetAllClientList;

  const fetchClientList = async ({
    adminId: uid,
    latitude: lat,
    longitude: lng,
  }) => {
    if (!uid) return null;
    const key = `${String(uid)}|${lat ?? ""}|${lng ?? ""}`;
    if (superAdminClientListCache.key === key) {
      return superAdminClientListCache.data ?? null;
    }
    try {
      const r = await api.post("/SuperAdmin/getClientList", {
        userid: uid,
        latitude: lat,
        longitude: lng,
      });
      const data = r?.data ?? null;
      superAdminClientListCache = { key, data };
      return data;
    } catch (error) {
      console.error("getClientList error:", error);
      return null;
    }
  };

  const globalSearchAllClientList = useCallback(
    async (globalSearch = "") => {
      const normalizedSearch = String(globalSearch || "").trim();

      if (!normalizedSearch) {
        return {
          responseCode: "00",
          responseMessage: "SUCCESS",
          response: [],
        };
      }

      try {
        const response = await api.post("/Admin/getGlobalSearchAllClientList", {
          global_search: normalizedSearch,
        });

        return response?.data || null;
      } catch (error) {
        console.error(
          "getGlobalSearchAllClientList error:",
          error?.response || error,
        );

        return {
          responseCode: "01",
          responseMessage:
            error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            "Could not search clients",
          response: [],
        };
      }
    },
    [api],
  );

  const fetchAllGateWay = async () => {
    try {
      const r = await api.post(`/Order/getAllActivePg`, {});
      if (r?.data?.responseCode === "00") {
        setGateWay(r.data.response || []);
      } else {
        toast.error(r?.data?.responseMessage || "Failed to fetch clients.");
      }
    } catch (e) {
      toast.error("Error fetching clients.");
      console.error("getAllClientList error:", e);
    }
  };

  useEffect(() => {
    if (sharedGatewayLoadPromise) return;
    sharedGatewayLoadPromise = fetchAllGateWay();
  }, [api, setGateWay]);

  const fetchCreditTopupReport = async ({ adminId, userId }) => {
    if (!adminId || !userId) return null;

    try {
      const r = await api.post("/SuperAdmin/v1.0/creditTopupReport", {
        adminId,
        userId,
      });

      return r?.data || null;
    } catch (error) {
      console.error("creditTopupReport error:", error);
      return null;
    }
  };

  const creditTopup = async ({
    adminId,
    userId,
    topupAmount,
    transactionReference,
    transactionRemarks,
  }) => {
    if (!adminId || !userId || !topupAmount) return null;

    try {
      const r = await api.post("/SuperAdmin/v1.0/creditTopup", {
        adminId,
        userId,
        topupAmount,
        transactionReference,
        transactionRemarks,
      });

      return r?.data || null;
    } catch (error) {
      console.error("creditTopup error:", error);
      return null;
    }
  };

  const toggleClientStatus = async ({ userid, status }) => {
    if (!userid || !status) return null;

    try {
      const r = await api.post("/SuperAdmin/v1.0/userActivateDeactivate", {
        userid,
        status,
      });

      return r?.data || null;
    } catch (error) {
      console.error("userActivateDeactivate error:", error);
      return null;
    }
  };

  const registerBeneficiaryV1 = async ({
    accountNumber,
    ifscCode,
    bankName,
    bankHolderName,
    mobileNumber,
    emailId,
  }) => {
    try {
      const r = await api2.post(
        "/api/Payout/AxisBank/BeneficiaryRegistration",
        {
          accountNumber,
          ifscCode,
          bankName,
          bankHolderName,
          mobileNumber,
          emailId,
        },
      );

      return r?.data || null;
    } catch (error) {
      console.error("BeneficiaryRegistration error:", error);
      return {
        error: true,
        data: error?.response?.data,
      };
    }
  };
    const registerBeneficiaryV2 = async ({
    accountNumber,
    ifscCode,
    bankName,
    bankHolderName,
    mobileNumber,
    emailId,
  }) => {
    try {
      const r = await api.post(
        "/Transactions/v1.0/axisBankBeneficiaryRegistration",
        {
          accountNumber,
          ifscCode,
          bankName,
          bankHolderName,
          mobileNumber,
          emailId,
        },
      );

      return r?.data || null;
    } catch (error) {
      console.error("BeneficiaryRegistration error:", error);
      return {
        error: true,
        data: error?.response?.data,
      };
    }
  };

  const saveRestaurantOrderDetails = async (payload) => {
    if (!payload?.adminId || !payload?.userid) return null;

    try {
      const r = await api.post(
        "/Admin/v1.0/insertRestaurantOrderDetails",
        payload,
      );

      return r?.data || null;
    } catch (error) {
      console.error("insertRestaurantOrderDetails error:", error);
      return null;
    }
  };

  const fetchClientConfigurationStatus = async (userid) => {
    if (!userid) return null;

    try {
      const r = await api.post("/Admin/v1.0/getClientConfigurationStatus", {
        userid,
      });

      return r?.data || null;
    } catch (error) {
      console.error("getClientConfigurationStatus error:", error);
      return null;
    }
  };

  const updatePaymentGateway = async ({ userid, paymentGateway }) => {
    setSpin(userid, "gateway");
    try {
      const r = await api.post(`/Admin/v1.0/updateClientPaymentGateway`, {
        adminId,
        userid,
        paymentGateway: paymentGateway?.trim(),
      });
      r?.data?.responseCode === "00"
        ? toast.success(r?.data?.responseMessage || "Gateway updated")
        : toast.error(r?.data?.responseMessage || "Gateway update failed");
    } catch {
      toast.error("Error updating gateway");
    } finally {
      clearSpin(userid);
    }
  };

  const getUserBankDetails = async ({ id, type }) => {
    if (!id && id !== 0) return null;

    try {
      const r = await api.post("/Admin/getUserBankDetails", {
        id,
        type: String(type),
      });

      return r?.data || null;
    } catch (error) {
      console.error("getUserBankDetails error:", error);
      return null;
    }
  };

  const createOrUpdateSubmerchant = async ({ fk_userid, submerchant_id }) => {
    if (!fk_userid) return null;

    try {
      const r = await api.post("/Admin/createOrUpdateSubmerchant", {
        fk_userid,
        submerchant_id: String(submerchant_id || "").trim(),
      });

      return r?.data || null;
    } catch (error) {
      console.error("createOrUpdateSubmerchant error:", error);
      return null;
    }
  };

  const getSubmerchant = async ({ fk_userid }) => {
    if (!fk_userid) return null;

    try {
      const r = await api.post("/Order/getSubmerchant", {
        fk_userid,
      });

      return r?.data || null;
    } catch (error) {
      console.error("getSubmerchant error:", error);
      return null;
    }
  };

  const getRestaurantPreciseLocation = async () => {
    try {
      const r = await api.post(
        "/Admin/getRestaurantDetailsPreciseLocation",
        {},
      );
      return r?.data || null;
    } catch (error) {
      console.error("getRestaurantDetailsPreciseLocation error:", error);
      return null;
    }
  };

  const updateRestaurantLatLong = async ({
    fk_user_id,
    latitude,
    longtitude,
  }) => {
    if (!fk_user_id) return null;

    try {
      const r = await api.post("/Admin/v1.0/UpdateLatitudeLongtitude", {
        fk_user_id: String(fk_user_id),
        latitude: String(latitude || "").trim(),
        longtitude: String(longtitude || "").trim(),
      });

      return r?.data || null;
    } catch (error) {
      console.error("UpdateLatitudeLongtitude error:", error);
      return null;
    }
  };

  const getOrganizationTypeAdmin = async () => {
    try {
      const r = await api.post("/Admin/GetOrganizationTypeAdmin", {});
      return r?.data || null;
    } catch (error) {
      console.error("GetOrganizationTypeAdmin error:", error);
      return null;
    }
  };

  const getAllOrgByTypeAdmin = async ({ orgType, active = "y" }) => {
    if (!orgType) return null;

    try {
      const r = await api.post("/Admin/v1.0/GetAllOrgByTypeAdmin", {
        orgType: String(orgType),
        active: String(active),
      });
      return r?.data || null;
    } catch (error) {
      console.error("GetAllOrgByTypeAdmin error:", error);
      return null;
    }
  };

  const onboardRestaurantStep = async ({
    stepKey,
    apiStep,
    userid,
    adminId,
    payload,
    showToast = true,
  }) => {
    if (!stepKey || !apiStep || !userid || !adminId) {
      if (showToast) {
        toast.error("adminId, userid or step missing");
      }

      return {
        success: false,
        stepKey,
        apiStep,
        data: null,
      };
    }

    const normalizedAdminId = String(adminId).trim();
    const normalizedUserid = String(userid).trim();
    const normalizedStep = String(apiStep).trim();

    // v5.0 API takes adminId, userid and step inside the JSON body.
    // Only the payload mapped to the active onboarding step is sent.
    const requestBody = {
      adminId: normalizedAdminId,
      userid: normalizedUserid,
      step: normalizedStep,
      [stepKey]: payload?.[stepKey] || {},
    };

    // Step 7: v5.0 API requires orderType as an array.
    // Keep this safeguard here as well so every caller sends the correct shape.
    if (stepKey === "updateOrderTypeOptions") {
      const step7Payload = payload?.updateOrderTypeOptions || {};
      const rawOrderType = step7Payload?.orderType;
      const orderType = Array.isArray(rawOrderType)
        ? rawOrderType
        : rawOrderType
          ? [rawOrderType]
          : [];

      requestBody.updateOrderTypeOptions = {
        ...step7Payload,
        orderType: orderType
          .map((item) => String(item || "").trim())
          .filter(Boolean),
        status: String(step7Payload?.status || "Y").trim(),
      };
    }

    // Step 9: userid is always required, but planId and days are mutually exclusive.
    // Even if a caller accidentally provides both, only one will be sent.
    if (stepKey === "changeSubscriptionPlanV2") {
      const step9Payload = payload?.changeSubscriptionPlanV2 || {};
      const planId = String(step9Payload?.planId || "").trim();
      const days = String(step9Payload?.days || "").trim();

      requestBody.changeSubscriptionPlanV2 = {
        userid: normalizedUserid,
        // API schema expects both keys to always be present.
        // Only one of planId / days will contain a value; the other stays empty.
        planId: days ? "" : planId,
        days: days || "",
      };
    }

    try {
      const response = await api.post(
        "/Admin/v5.0/onboardRestaurant",
        requestBody,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      const responseData = response?.data || null;

      const success =
        responseData?.responseCode === "00" ||
        responseData?.status === "0" ||
        responseData?.success === true;

      if (showToast) {
        if (success) {
          toast.success(
            responseData?.responseMessage || `Step ${apiStep} saved`,
          );
        } else {
          toast.error(
            responseData?.responseMessage || `Step ${apiStep} failed`,
          );
        }
      }

      return {
        success,
        stepKey,
        apiStep,
        data: responseData,
      };
    } catch (error) {
      console.error("onboardRestaurantStep error:", error?.response || error);

      if (showToast) {
        toast.error(
          error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            error?.response?.data?.title ||
            `Step ${apiStep} API error`,
        );
      }

      return {
        success: false,
        stepKey,
        apiStep,
        data: error?.response?.data || null,
      };
    }
  };

  const getOnboardedRestaurantDetails = async ({ userId = "" } = {}) => {
    const normalizedUserId = String(userId || "").trim();

    if (!normalizedUserId) {
      return {
        responseCode: "01",
        responseMessage: "User ID is required",
        response: [],
      };
    }

    try {
      const response = await api.post(
        "/Admin/v5.0/getOnboardedRestaurantDetails",
        {
          userId: normalizedUserId,
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      return response?.data || null;
    } catch (error) {
      console.error(
        "getOnboardedRestaurantDetails error:",
        error?.response || error,
      );

      return {
        responseCode: "01",
        responseMessage:
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Could not load onboarding details",
        response: [],
      };
    }
  };
const getRestaurantLocation = useCallback(async (payload = {}) => {
  try {
    const r = await api.post("/Admin/getRestaurantLocation", {});
    return r?.data || null;
  } catch (error) {
    console.error("getRestaurantLocation error:", error);
    return null;
  }
}, [api]);

const updateOrganizationLocation = useCallback(
  async ({ pk_info_id, location }) => {
    if (!pk_info_id || location === undefined || location === null) {
      return null;
    }
    try {
      const r = await api.post(
        "/Admin/updateOrganizationLocation",
        {
          pk_info_id: String(pk_info_id),
          location: String(location),
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );
      return r?.data || null;
    } catch (error) {
      console.error("updateOrganizationLocation error:", error);
      return null;
    }
  },
  [api],
);

  return {
    clients: AllClients,

    loading,
    fetchAll,
    busy,
    fmt,
    // actions
    fetchClientList,
    globalSearchAllClientList,
    fetchCreditTopupReport,
    creditTopup,
    toggleClientStatus,
    registerBeneficiaryV1,
    registerBeneficiaryV2,
    saveRestaurantOrderDetails,
    fetchClientConfigurationStatus,
    updatePaymentGateway,
    fetchAllGateWay,
    getUserBankDetails,
    createOrUpdateSubmerchant,
    getSubmerchant,
    getRestaurantPreciseLocation,
    updateRestaurantLatLong,
    getOrganizationTypeAdmin,
    getAllOrgByTypeAdmin,
    onboardRestaurantStep,
    getOnboardedRestaurantDetails,
    getRestaurantLocation,
  updateOrganizationLocation,
  };
}
